import { Box, Textarea } from '@chakra-ui/react';
import { Navigate } from 'react-router-dom';
import {
  ArrowCta,
  Button,
  FormControl,
  Heading,
  Text,
  VStack,
} from 'components/ui';
import HeaderNav from '../HeaderNav/HeaderNav';
import { VisitCard } from '../ui/VisitCard';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';
import { useFeedbackPage } from './useFeedbackPage';
import PhoneNumberForm from 'components/PhoneNumberView/PhoneNumberForm';

const noteFieldProps = {
  bg: 'rgba(4, 51, 56, 0.05)',
  borderColor: 'transparent',
  borderRadius: '8px',
  borderWidth: '1px',
  fontFamily: 'body',
  fontSize: '14px',
  minH: '96px',
  px: '15px',
  py: '12px',
  resize: 'vertical',
  w: '100%',
  focusRing: 'none',
  _focus: {
    bg: 'white',
    borderColor: 'secondary.200',
    boxShadow: '0 0 0 3px rgba(45, 102, 112, 0.14)',
    outline: 'none',
  },
  _focusVisible: {
    bg: 'white',
    borderColor: 'secondary.200',
    boxShadow: '0 0 0 3px rgba(45, 102, 112, 0.14)',
    outline: 'none',
  },
} as const;

export const FeedbackPage = () => {
  const {
    bookedMeeting,
    feedbackTxt,
    isLoading,
    setFeedbackTxt,
    onSkip,
    redirectToHome,
    timezone,
    collectPhoneNumber,
    phoneNumber,
    agreeChecked,
    setPhoneNumber,
    setAgreeChecked,
    phoneValid,
    feedbackValid,
    handleSubmit,
  } = useFeedbackPage();

  if (redirectToHome) return <Navigate to={'/'} replace />;

  const { provider } = bookedMeeting || {};

  const disableCta = collectPhoneNumber
    ? !feedbackValid && !phoneValid
    : !feedbackValid;

  return (
    <StickyCtaLayout
      actionArea={
        <>
          <ArrowCta
            disabled={disableCta}
            loading={isLoading}
            onClick={handleSubmit}
          >
            Share With My Care Team
          </ArrowCta>

          <Button
            bgColor={'transparent'}
            disabled={isLoading}
            h={'auto'}
            minH={'auto'}
            onClick={onSkip}
            py={0}
            variant={'ghost'}
            mt={4}
          >
            <Text
              color={'secondary.500'}
              fontSize={'14px'}
              fontWeight={600}
              textDecoration={'underline'}
            >
              Skip for now
            </Text>
          </Button>
        </>
      }
      header={<HeaderNav />}
      maxWidth="formWidth"
    >
      <VStack align="center" gap={'22px'} w="100%">
        <Heading
          color={'secondary.500'}
          textStyle="heading.h2"
          pt={6}
          textAlign="center"
        >
          Your visit is booked!
        </Heading>

        <Box
          borderColor={'fieldBorder.200'}
          borderWidth={1}
          bg={'white'}
          px={'20px'}
          rounded={'panel'}
          w={'100%'}
        >
          {provider && (
            <VisitCard
              avatarUrl={provider.image_url}
              hostDisplayName={provider.display_name}
              meetingTime={bookedMeeting?.meeting_date ?? ''}
              timezone={timezone}
            />
          )}

          {collectPhoneNumber && (
            <Box py={4.5} borderBottomWidth={1}>
              <PhoneNumberForm
                agreed={agreeChecked}
                label="Add your mobile number for appointment reminders"
                phoneNumber={phoneNumber}
                setAgreed={setAgreeChecked}
                setPhoneNumber={setPhoneNumber}
              />
            </Box>
          )}

          <VStack
            alignItems={'stretch'}
            borderTopColor={'fieldBorder.200'}
            gap={'8px'}
            py={'18px'}
          >
            <FormControl>
              <FormControl.Label>
                Optional: share with your care team what you hope to get our of
                your visit.
              </FormControl.Label>

              <Textarea
                {...noteFieldProps}
                disabled={isLoading}
                value={feedbackTxt}
                onChange={(e) => setFeedbackTxt(e.target.value)}
              />
            </FormControl>
          </VStack>
        </Box>
      </VStack>
    </StickyCtaLayout>
  );
};
